import { isNull, isUndefined } from 'lodash/fp';
import React, { MouseEvent, useCallback, useMemo, useState } from 'react';

import { useWindowResize } from '../../../../hooks/useWindowResize';
import { CalculatorField } from '../../../admin/calculator/store';
import { CalculatorRiskField, RiskAssociated, RiskAssociatedValue } from '../../../admin/risk-tolerance/store';
import { riskAssociatedLabel } from '../../../admin/risk-tolerance/utils';
import { ReadOnlyCalculatorField } from '../../../datasets/fields/ReadOnlyCalculatorField';
import { IconButton } from '../../button/IconButton';
import { PlusButton } from '../../button/PlusButton';
import { newCalculatorField } from '../../calculator/constants';
import { Icon } from '../../icon/Icon';
import { Calculator as CalculatorIcon, CaretDown, CaretSide } from '../../icons';
import { CalculatorRiskFieldModal } from './CalculatorRiskFieldModal';
import styles from './RiskField.module.scss';
import { DeleteButton } from '../../button/DeleteButton';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

const { lightGrey } = styles;

const SCREEN_MARGIN = 20;

interface CalculatorRiskFieldConfigProps {
    riskField: CalculatorRiskField;
    riskAssociated: RiskAssociated;
    updateRiskConfig: (riskAssociated: RiskAssociated, value: RiskAssociatedValue) => void;
}

export const CalculatorRiskFieldConfig: React.FC<CalculatorRiskFieldConfigProps> = ({ riskField, riskAssociated, updateRiskConfig }) => {
    const [isOpenSection, setIsOpenSection] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [indexToDelete, setIndexToDelete] = useState<number | null>(null);

    const [screenWidth] = useWindowResize();

    const { riskConfig, showGracePeriod, showOutput, calculatorFieldType, label } = riskField;

    const modalWidth = useMemo(() => (screenWidth - SCREEN_MARGIN) * 0.6, [screenWidth]);

    const sectionOpenIcon = useMemo(() => isOpenSection ? CaretDown : CaretSide, [isOpenSection]);
    const toggleSectionOpen = useCallback(() => setIsOpenSection(!isOpenSection), [isOpenSection]);

    const riskAssociatedExamples = useMemo(() => riskConfig[riskAssociated], [riskConfig, riskAssociated]);

    const sectionTitle = useMemo(() => `${riskAssociatedExamples.length} Example${riskAssociatedExamples.length === 0 || riskAssociatedExamples.length > 1 ? 's' : ''}`, [riskAssociatedExamples.length]);

    const createNewExample = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsOpenSection(true);
        setSelectedIndex(riskAssociatedExamples.length);
    }, [riskAssociatedExamples.length]);

    const selectedRiskExample = useMemo(() => {
        if (isNull(selectedIndex)) {
            return null;
        }
        return riskAssociatedExamples[selectedIndex] || [newCalculatorField()];
    }, [selectedIndex, riskAssociatedExamples]);

    const riskToDelete = useMemo(() => {
        if (isNull(indexToDelete)) {
            return null;
        }
        const toDelete = riskAssociatedExamples[indexToDelete];
        return (
            <div className={styles.readOnlyCalculatorField}>
                {toDelete.map(({ calculation, calculationCondition, output, gracePeriod }, calculationIndex) => (
                    <ReadOnlyCalculatorField
                        key={calculationIndex}
                        value={toDelete}
                        calculation={calculation}
                        output={output}
                        gracePeriod={gracePeriod}
                        calculationCondition={calculationCondition}
                        calculationIndex={calculationIndex}
                        showOutputField={showOutput}
                        showGracePeriod={showGracePeriod}
                        borderColor={lightGrey}
                    />
                ))}
            </div>
        );
    }, [indexToDelete, riskAssociatedExamples, showGracePeriod, showOutput]);

    const closeExample = useCallback(() => setSelectedIndex(null), []);

    const saveExample = useCallback((updatedValue: CalculatorField[]) => {
        let updatedConfig = riskAssociatedExamples;
        if (!isNull(selectedIndex)) {
            if (!isUndefined(riskAssociatedExamples[selectedIndex])) {
                updatedConfig = riskAssociatedExamples.map((example, index) => index === selectedIndex ? updatedValue : example);
            } else {
                updatedConfig = [...riskAssociatedExamples, updatedValue];
            }
        }
        updateRiskConfig(riskAssociated, updatedConfig);
        closeExample();
    }, [riskAssociatedExamples, selectedIndex, riskAssociated, closeExample, updateRiskConfig]);

    const removeExample = useCallback(() => {
        if (!isNull(indexToDelete)) {
            updateRiskConfig(riskAssociated, riskAssociatedExamples.filter((_, i) => i !== indexToDelete));
        }
        setIndexToDelete(null);
    }, [riskAssociatedExamples, riskAssociated, updateRiskConfig, indexToDelete]);

    const showConfirmDelete = useCallback((index: number) => setIndexToDelete(index), []);
    const closeConfirmDelete = useCallback(() => setIndexToDelete(null), []);

    return (
        <div className={styles.riskCalculatorWrapper}>
            <div className={styles.sectionHeader} onClick={toggleSectionOpen}>
                <div className={styles.sectionTitle}>
                    <div className={styles.sectionOpenIcon}>
                        <Icon icon={sectionOpenIcon} fontSize={16} />
                    </div>
                    <div className={styles.sectionLabel}>{sectionTitle}</div>
                </div>
                <div className={styles.addNewExampleWrapper}>
                    <PlusButton onClick={createNewExample} fontSize={20} />
                </div>
            </div>
            {isOpenSection &&
                <div className={styles.riskExamplesWrapper}>
                    {riskAssociatedExamples.map((value, index) => (
                        <div key={index} className={styles.riskExample}>
                            <div className={styles.readOnlyCalculatorField}>
                                {value.map(({ calculation, calculationCondition, output, gracePeriod }, calculationIndex) => (
                                    <ReadOnlyCalculatorField
                                        key={calculationIndex}
                                        value={value}
                                        calculation={calculation}
                                        output={output}
                                        gracePeriod={gracePeriod}
                                        calculationCondition={calculationCondition}
                                        calculationIndex={calculationIndex}
                                        showOutputField={showOutput}
                                        showGracePeriod={showGracePeriod}
                                        borderColor={lightGrey}
                                    />
                                ))}
                            </div>
                            <div className={styles.iconWrapper}>
                                <IconButton icon={CalculatorIcon} onClick={() => setSelectedIndex(index)} fontSize={30} overlayText='Edit Example' />
                                <DeleteButton onClick={() => showConfirmDelete(index)} />
                            </div>
                        </div>
                    ))}
                </div>
            }
            {!isNull(selectedRiskExample) &&
                <CalculatorRiskFieldModal
                    closeModal={closeExample}
                    fieldType={calculatorFieldType}
                    showGracePeriod={showGracePeriod}
                    showOutput={showOutput}
                    initialCalculatorValue={selectedRiskExample}
                    saveCalculatorExample={saveExample}
                    label={label}
                    width={modalWidth}
                    riskAssociated={riskAssociatedLabel[riskAssociated]}
                />
            }
            {!isNull(indexToDelete) &&
                <DeleteConfirmationModal
                    deleteExample={removeExample}
                    closeModal={closeConfirmDelete}
                    icon={CalculatorIcon}
                >
                    {riskToDelete}
                </DeleteConfirmationModal>
            }
        </div>
    );
};
